import React, { useState, useEffect } from 'react';
import Header from '../../components/layout/Header';
import ErrorBanner from '../../components/ui/ErrorBanner';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import apiClient from '../../api/apiClient';

const TABS = ['Overview', 'By Business', 'By Ops Manager'];
const fmt = (v) => '$' + Math.round(v || 0).toLocaleString();
const pct = (a, b) => b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0;

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={s.kpiCard}>
      <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.375rem' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: '800', color: color || '#1e293b' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.125rem' }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ value, max, color }) {
  const p = pct(value, max);
  return (
    <div style={s.progressBg}>
      <div style={{ ...s.progressFill, width: `${p}%`, background: color || '#6366f1' }} />
    </div>
  );
}

// ── Overview Tab ────────────────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/superadmin/overview')
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  const totals = data.totals || {};
  const accounts = data.accounts || [];

  return (
    <div>
      <div style={s.kpiRow}>
        <KpiCard label="Total Businesses" value={accounts.length} />
        <KpiCard label="Total Employees" value={(totals.totalEmployees || 0).toLocaleString()} />
        <KpiCard label="YTD Revenue" value={fmt(totals.totalRevenue)} color="#6366f1" />
        <KpiCard label="Open Plans" value={totals.totalOpenPlans || 0} color="#f59e0b" />
        <KpiCard label="Avg HS Compliance" value={(totals.avgCompliance || 0) + '%'} color={totals.avgCompliance >= 80 ? '#22c55e' : '#dc2626'} />
      </div>

      <h3 style={s.sectionTitle}>All Businesses</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
        {accounts.map(acc => (
          <div key={acc.id} style={s.bizCard}>
            <div style={s.bizCardHeader}>
              <h4 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>{acc.name}</h4>
              <span style={s.badge}>{acc.employeeCount} employees</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem', marginTop: '0.875rem' }}>
              <div style={s.miniStat}>
                <div style={s.miniLabel}>Revenue</div>
                <div style={s.miniVal}>{fmt(acc.ytdRevenue)}</div>
              </div>
              <div style={s.miniStat}>
                <div style={s.miniLabel}>HS Compliance</div>
                <div style={{ ...s.miniVal, color: (acc.hsCompliance || 0) >= 80 ? '#22c55e' : '#dc2626' }}>{acc.hsCompliance || 0}%</div>
              </div>
              <div style={s.miniStat}>
                <div style={s.miniLabel}>Open Plans</div>
                <div style={s.miniVal}>{acc.openPlans || 0}</div>
              </div>
              <div style={s.miniStat}>
                <div style={s.miniLabel}>Users</div>
                <div style={s.miniVal}>{acc.userCount || 0}</div>
              </div>
            </div>
            {acc.ytdRevenue > 0 && acc.revenueBudget > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
                  <span>Revenue Progress</span>
                  <span>{pct(acc.ytdRevenue, acc.revenueBudget)}%</span>
                </div>
                <ProgressBar value={acc.ytdRevenue} max={acc.revenueBudget} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const BIZ_SUBTABS = ['Managers', 'Business Units'];

// ── By Business Tab ─────────────────────────────────────────────────────────────
function ByBusinessTab() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [managers, setManagers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [subTab, setSubTab] = useState(0);

  useEffect(() => {
    apiClient.get('/superadmin/accounts')
      .then(res => { setAccounts(res.data.accounts || []); })
      .catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const selectAccount = async (acc) => {
    setSelected(acc); setDetailLoading(true); setManagers([]); setUnits([]);
    try {
      const [mgrRes, buRes] = await Promise.allSettled([
        apiClient.get(`/superadmin/account/${acc.id}/managers`),
        apiClient.get(`/superadmin/account/${acc.id}/business-units`),
      ]);
      if (mgrRes.status === 'fulfilled') setManagers(mgrRes.value.data.managers || []);
      if (buRes.status === 'fulfilled') setUnits(buRes.value.data.units || []);
    } catch {}
    finally { setDetailLoading(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '280px 1fr' : '1fr', gap: '1.25rem' }}>
      <div>
        <h4 style={s.sectionTitle}>Businesses ({accounts.length})</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {accounts.map(acc => (
            <div
              key={acc.id}
              onClick={() => selectAccount(acc)}
              style={{ ...s.accItem, ...(selected?.id === acc.id ? s.accItemActive : {}) }}
            >
              <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{acc.name}</div>
              <div style={{ fontSize: '0.75rem', color: selected?.id === acc.id ? 'rgba(255,255,255,0.7)' : '#94a3b8', marginTop: '0.125rem' }}>
                {acc.userCount} users · {acc.employeeCount} employees
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', background: '#f8fafc', padding: '0.25rem', borderRadius: '0.5rem', width: 'fit-content' }}>
            {BIZ_SUBTABS.map((tab, i) => (
              <button
                key={tab}
                onClick={() => setSubTab(i)}
                style={{ padding: '0.35rem 0.875rem', background: subTab === i ? '#6366f1' : 'transparent', color: subTab === i ? '#fff' : '#64748b', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
              >
                {tab}
              </button>
            ))}
          </div>

          {detailLoading ? <LoadingSpinner /> : (
            <>
              {subTab === 0 && (
                <>
                  <h4 style={s.sectionTitle}>Ops Managers — {selected.name}</h4>
                  {managers.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No managers found for this business.</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.875rem' }}>
                      {managers.map(mgr => (
                        <div key={mgr.id} style={s.mgrCard}>
                          <div style={s.mgrAvatar}>{(mgr.name || mgr.email)?.[0]?.toUpperCase()}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '700', fontSize: '0.875rem', color: '#1e293b' }}>{mgr.name || mgr.email}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.125rem' }}>Team: {mgr.teamSize || 0} employees</div>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.75rem' }}>
                              <div><span style={{ color: '#94a3b8' }}>1-on-1s this week:</span> <strong>{mgr.onoWeek || 0}</strong></div>
                              <div><span style={{ color: '#94a3b8' }}>YTD:</span> <strong>{mgr.onoYtd || 0}</strong></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {subTab === 1 && (
                <>
                  <h4 style={s.sectionTitle}>Business Units — {selected.name}</h4>
                  {units.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No business units found.</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                      {units.map(bu => (
                        <div key={bu.id} style={{ ...s.bizCard, opacity: bu.active ? 1 : 0.5 }}>
                          <div style={{ fontWeight: '700', fontSize: '0.875rem', color: '#1e293b', marginBottom: '0.5rem' }}>{bu.name}</div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div style={s.miniStat}>
                              <div style={s.miniLabel}>Assigned Users</div>
                              <div style={s.miniVal}>{bu.assignedUsers || 0}</div>
                            </div>
                            <div style={{ ...s.miniStat, background: bu.active ? '#ecfdf5' : '#fef2f2', border: 'none' }}>
                              <div style={s.miniLabel}>Status</div>
                              <div style={{ ...s.miniVal, color: bu.active ? '#22c55e' : '#dc2626', fontSize: '0.75rem' }}>{bu.active ? 'Active' : 'Inactive'}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── By Ops Manager Tab ──────────────────────────────────────────────────────────
function ByManagerTab() {
  const [accounts, setAccounts] = useState([]);
  const [allManagers, setAllManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/superadmin/accounts')
      .then(async res => {
        const accs = res.data.accounts || [];
        setAccounts(accs);
        const results = await Promise.allSettled(
          accs.map(acc => apiClient.get(`/superadmin/account/${acc.id}/managers`).then(r => r.data.managers?.map(m => ({ ...m, accountName: acc.name })) || []))
        );
        const flat = results.flatMap(r => r.status === 'fulfilled' ? r.value : []);
        setAllManagers(flat);
      })
      .catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} />;

  // Group by account
  const byAccount = {};
  for (const acc of accounts) {
    byAccount[acc.id] = { acc, managers: allManagers.filter(m => m.accountName === acc.name) };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {Object.values(byAccount).map(({ acc, managers }) => (
        <div key={acc.id}>
          <h4 style={{ margin: '0 0 0.75rem 0', fontWeight: '700', color: '#1e293b', fontSize: '0.95rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
            🏢 {acc.name}
          </h4>
          {managers.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>No managers</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {managers.map(mgr => (
                <div key={mgr.id} style={s.mgrRow}>
                  <div style={s.mgrAvatar}>{(mgr.name || mgr.email)?.[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1, display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: '150px' }}>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>{mgr.name || mgr.email}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{mgr.email}</div>
                    </div>
                    <div style={s.mgrStat}><div style={s.miniLabel}>Team Size</div><div style={s.miniVal}>{mgr.teamSize || 0}</div></div>
                    <div style={s.mgrStat}><div style={s.miniLabel}>1-on-1s This Week</div><div style={{ ...s.miniVal, color: '#6366f1' }}>{mgr.onoWeek || 0}</div></div>
                    <div style={s.mgrStat}><div style={s.miniLabel}>YTD 1-on-1s</div><div style={s.miniVal}>{mgr.onoYtd || 0}</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {Object.keys(byAccount).length === 0 && (
        <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>No businesses found</div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function SuperDashboard() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      <Header title="Super Admin Dashboard" />
      <div style={s.page}>
        <div style={s.tabBar}>
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              style={{ ...s.tabBtn, ...(activeTab === i ? s.tabBtnActive : {}) }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div style={s.content}>
          {activeTab === 0 && <OverviewTab />}
          {activeTab === 1 && <ByBusinessTab />}
          {activeTab === 2 && <ByManagerTab />}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { padding: '1.5rem 1.75rem' },
  content: { background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  tabBar: { display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', background: '#fff', padding: '0.375rem', borderRadius: '0.625rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  tabBtn: { padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#64748b' },
  tabBtnActive: { background: '#6366f1', color: '#fff' },
  kpiRow: { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  kpiCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1rem 1.25rem', flex: '1 1 120px', textAlign: 'center' },
  sectionTitle: { margin: '0 0 0.875rem 0', fontSize: '0.9rem', fontWeight: '700', color: '#374151' },
  bizCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1.125rem' },
  bizCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge: { background: '#ede9fe', color: '#6366f1', padding: '0.2rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' },
  miniStat: { background: '#fff', border: '1px solid #f1f5f9', borderRadius: '0.5rem', padding: '0.5rem 0.625rem' },
  miniLabel: { fontSize: '0.65rem', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' },
  miniVal: { fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', marginTop: '0.125rem' },
  progressBg: { height: '6px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '999px', transition: 'width 0.3s ease' },
  accItem: { padding: '0.75rem 0.875rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.625rem', cursor: 'pointer', transition: 'all 0.15s' },
  accItemActive: { background: '#6366f1', border: '1px solid #6366f1', color: '#fff' },
  mgrCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.625rem', padding: '0.875rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' },
  mgrRow: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.625rem', padding: '0.875rem', display: 'flex', gap: '0.75rem', alignItems: 'center' },
  mgrAvatar: { width: '2.25rem', height: '2.25rem', borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1rem', flexShrink: 0 },
  mgrStat: { textAlign: 'center', minWidth: '80px' },
};
